import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Crosshair, Loader2, Play, Plus, Trash2 } from 'lucide-react'

import { ChoiceTile } from '@/components/ChoiceTile'
import { FieldLabel } from '@/components/FieldLabel'
import { TokenInput } from '@/components/TokenInput'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { AdUnit } from '@/services/adUnits/adUnitsApi'
import {
  DIAGNOSTIC_REQUEST_TYPES,
  DiagnosticsApi,
  type DiagnosticKeyValue,
  type DiagnosticRequest,
  type DiagnosticRequestType,
  type DiagnosticResult,
} from '@/services/diagnostics/diagnosticsApi'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'
import type { Targeting } from '@/services/targeting/targeting'
import { isIpv4Address } from '@/utils/ip'
import { DiagnosticResultCard } from './DiagnosticResultCard'

/** How many line items one by-id lookup asks for. */
const LINE_ITEM_CHUNK = 100

export const EMPTY_KEY_VALUE: DiagnosticKeyValue = { key: '', values: [] }

/** What the diagnostics form holds. Numbers stay text while they are typed. */
export interface DiagnosticFormValues {
  requestType?: DiagnosticRequestType
  categories: string[]
  segments: string[]
  keywords: string[]
  keyValues: DiagnosticKeyValue[]
  latitude: string
  longitude: string
  context: string
  ip: string
}

export const EMPTY_DIAGNOSTIC_FORM: DiagnosticFormValues = {
  categories: [],
  segments: [],
  keywords: [],
  keyValues: [EMPTY_KEY_VALUE],
  latitude: '',
  longitude: '',
  context: '',
  ip: '',
}

/** The targeting fields, which a link carrying any of them opens with showing. */
export const DIAGNOSTIC_TARGETING_FIELDS = [
  'categories',
  'segments',
  'keywords',
  'keyValues',
  'latitude',
  'longitude',
  'context',
  'ip',
] as const satisfies readonly (keyof DiagnosticFormValues)[]

const unique = (values: string[]) => [...new Set(values.map((v) => v.trim()).filter(Boolean))]

/**
 * A diagnostic request a line item's targeting would match: every category,
 * keyword, segment and key-value it asks for, the first article or domain as
 * the context and the first plain IP address. Exclusions are left out, since
 * a request only matches them by not carrying them; key-values that match any
 * value have nothing to send.
 */
export function diagnosticTargetingFrom(targeting: Targeting): Partial<DiagnosticFormValues> {
  const keyValues = new Map<string, string[]>()
  for (const target of targeting.keyValueTargets ?? [])
    for (const [key, values] of Object.entries(target.entries ?? {})) {
      if (key.startsWith('NOT ')) continue
      const flat = (values as (string | string[])[]).flat()
      if (flat.length) keyValues.set(key, unique([...(keyValues.get(key) ?? []), ...flat]))
    }

  const article = targeting.articleTarget?.negated ? undefined : targeting.articleTarget?.urls?.[0]
  const domain = targeting.domainNameTarget?.negated
    ? undefined
    : targeting.domainNameTarget?.names?.[0]
  const ip = targeting.ipAddressTarget?.addresses?.find(isIpv4Address)

  return {
    categories: unique((targeting.categoryTargets ?? []).flatMap((t) => t.categories ?? [])),
    segments: unique(
      (targeting.userSegmentTargets ?? []).flatMap((t) => (t.userSegments ?? []).map((s) => s.id))
    ),
    keywords: unique((targeting.keywordTargets ?? []).flatMap((t) => t.keywords ?? [])),
    keyValues: keyValues.size
      ? [...keyValues].map(([key, values]) => ({ key, values }))
      : [EMPTY_KEY_VALUE],
    context: article ?? (domain ? `https://${domain}/` : ''),
    ip: ip ?? '',
  }
}

/** How many of the targeting fields are filled in. */
export function diagnosticTargetingCount(values: DiagnosticFormValues): number {
  return [
    values.categories.length,
    values.segments.length,
    values.keywords.length,
    values.keyValues.some((kv) => kv.key.trim() && kv.values.length),
    values.latitude.trim() || values.longitude.trim(),
    values.context.trim(),
    values.ip.trim(),
  ].filter(Boolean).length
}

/** A run of the form: the request, and the ad units it goes to. */
interface Submission {
  id: number
  request: DiagnosticRequest
  adUnits: AdUnit[]
}

/** Where one ad unit's request stands in a run. */
interface Outcome {
  status: 'queued' | 'running' | 'done' | 'failed'
  result?: DiagnosticResult
  error?: unknown
}

export interface AdRequestDiagnosticsProps {
  values: DiagnosticFormValues
  onChange: (patch: Partial<DiagnosticFormValues>) => void
  /** The ad units the request is made on. */
  adUnits: AdUnit[]
  /** The ad units are still being looked up, so a run has to wait. */
  adUnitsLoading?: boolean
  /** Narrows the results to one line item. */
  lineItemId?: string
  /** Shown first, for picking the ad units; left out when they are given. */
  adUnitPicker?: ReactNode
  /** Shown beside the request type, for picking the line item; left out when it is given. */
  lineItemPicker?: ReactNode
  /** A line under the targeting title, e.g. where its values came from. */
  targetingHint?: string
  /** Whether the targeting starts open; by default, when anything is filled in. */
  targetingDefaultOpen?: boolean
}

/**
 * Tests an ad request on ad units, to see which line items would deliver and
 * why the rest would not: the request type, the targeting the request
 * carries, and one result card per ad unit.
 */
export function AdRequestDiagnostics({
  values,
  onChange,
  adUnits,
  adUnitsLoading,
  lineItemId,
  adUnitPicker,
  lineItemPicker,
  targetingHint,
  targetingDefaultOpen,
}: AdRequestDiagnosticsProps) {
  const { t } = useTranslation()
  const {
    requestType,
    categories,
    segments,
    keywords,
    keyValues,
    latitude,
    longitude,
    context,
    ip,
  } = values

  const [submission, setSubmission] = useState<Submission>()

  const targetingCount = diagnosticTargetingCount(values)
  const [targetingOpen, setTargetingOpen] = useState(
    () => targetingDefaultOpen ?? targetingCount > 0
  )

  const ipError = ip.trim() && !isIpv4Address(ip) ? t('diagnostics.invalidIp') : undefined
  const latLongError = (() => {
    const lat = latitude.trim()
    const long = longitude.trim()
    if (!lat && !long) return undefined
    if (!lat || !long) return t('diagnostics.latLongBoth')
    if (isNaN(Number(lat)) || isNaN(Number(long))) return t('diagnostics.latLongNumber')
    return undefined
  })()
  const canRun = !!requestType && adUnits.length > 0 && !adUnitsLoading && !ipError && !latLongError

  const run = () => {
    if (!canRun) return
    setSubmission({
      id: (submission?.id ?? 0) + 1,
      adUnits,
      request: {
        requestType,
        lineItemId,
        categories,
        segments,
        keywords,
        keyValues,
        latitude: latitude.trim() ? Number(latitude) : undefined,
        longitude: longitude.trim() ? Number(longitude) : undefined,
        context,
        ip,
      },
    })
  }

  // The ad server takes one diagnostic request at a time, so the ad units are
  // run one after another, each card filling in as its answer arrives.
  const [outcomes, setOutcomes] = useState<Outcome[]>([])
  useEffect(() => {
    if (!submission) return
    let cancelled = false
    const update = (index: number, outcome: Outcome) =>
      setOutcomes((current) => current.map((o, i) => (i === index ? outcome : o)))
    setOutcomes(submission.adUnits.map(() => ({ status: 'queued' })))
    ;(async () => {
      for (const [index, adUnit] of submission.adUnits.entries()) {
        if (cancelled) return
        update(index, { status: 'running' })
        try {
          const result = await DiagnosticsApi.run(adUnit.tagId, submission.request)
          if (!cancelled) update(index, { status: 'done', result })
        } catch (error) {
          if (!cancelled) update(index, { status: 'failed', error })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [submission])
  const running = outcomes.some((o) => o.status === 'queued' || o.status === 'running')

  // The ad server answers with line item ids only, so their names are looked up
  // once the run is over.
  const lineItemIds = useMemo(() => {
    const ids = new Set<string>()
    for (const outcome of outcomes)
      for (const participant of outcome.result?.lineItemParticipants ?? [])
        ids.add(participant.externalId)
    return [...ids].sort()
  }, [outcomes])
  const { data: lineItemNames = new Map<string, string>() } = useQuery({
    queryKey: ['diagnostics-line-items', lineItemIds.join(',')],
    enabled: !running && lineItemIds.length > 0,
    queryFn: async () => {
      const chunks: string[][] = []
      for (let i = 0; i < lineItemIds.length; i += LINE_ITEM_CHUNK)
        chunks.push(lineItemIds.slice(i, i + LINE_ITEM_CHUNK))
      const pages = await Promise.all(chunks.map((ids) => LineItemsApi.getByIds(ids)))
      return new Map(pages.flat().map((lineItem) => [lineItem.id, lineItem.name]))
    },
  })

  const setKeyValues = (rows: DiagnosticKeyValue[]) => onChange({ keyValues: rows })
  const updateKeyValue = (index: number, next: DiagnosticKeyValue) =>
    setKeyValues(keyValues.map((kv, i) => (i === index ? next : kv)))

  const requestTypeCard = (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('diagnostics.requestType')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            'grid gap-3 sm:grid-cols-3',
            lineItemPicker && 'md:grid-cols-1 lg:grid-cols-3'
          )}
        >
          {DIAGNOSTIC_REQUEST_TYPES.map((type) => (
            <ChoiceTile
              key={type}
              title={t(`diagnostics.requestTypes.${type}.name`)}
              description={t(`diagnostics.requestTypes.${type}.summary`)}
              selected={requestType === type}
              onSelect={() => onChange({ requestType: type })}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )

  return (
    // Inside an edit page's form, Enter in a field here must not save the page.
    <div
      className="flex flex-col gap-6"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement)
          event.preventDefault()
      }}
    >
      {adUnitPicker}

      {lineItemPicker ? (
        <div className="grid gap-6 md:grid-cols-2">
          {requestTypeCard}
          {lineItemPicker}
        </div>
      ) : (
        requestTypeCard
      )}

      <Card tone="insight">
        <Collapsible open={targetingOpen} onOpenChange={setTargetingOpen}>
          <CardHeader>
            <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CardIcon icon={Crosshair} />
                {t('diagnostics.targeting')}
              </CardTitle>
              {/* Filled-in fields keep counting while closed, so none go unnoticed. */}
              {!targetingOpen && targetingCount > 0 && (
                <Badge variant="secondary">{targetingCount}</Badge>
              )}
              <ChevronDown
                className={cn(
                  'ml-auto size-4 text-muted-foreground transition-transform',
                  targetingOpen && 'rotate-180'
                )}
              />
            </CollapsibleTrigger>
            {targetingHint && <p className="text-xs text-muted-foreground">{targetingHint}</p>}
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="p-field">
                <FieldLabel>{t('targeting.types.category.plural')}</FieldLabel>
                <TokenInput
                  value={categories}
                  onChange={(next) => onChange({ categories: next })}
                  placeholder={t('targeting.types.category.placeholder')}
                />
              </div>
              <div className="p-field">
                <FieldLabel>{t('targeting.types.userSegment.plural')}</FieldLabel>
                <TokenInput value={segments} onChange={(next) => onChange({ segments: next })} />
              </div>
              <div className="p-field">
                <FieldLabel>{t('targeting.types.keyword.plural')}</FieldLabel>
                <TokenInput
                  value={keywords}
                  onChange={(next) => onChange({ keywords: next })}
                  placeholder={t('targeting.types.keyword.placeholder')}
                />
              </div>
              <div className="p-field">
                <FieldLabel tooltip={t('diagnostics.contextHelp')} htmlFor="diagnostics-context">
                  {t('diagnostics.context')}
                </FieldLabel>
                <Input
                  id="diagnostics-context"
                  value={context}
                  onChange={(event) => onChange({ context: event.target.value })}
                  placeholder="https://example.com/article"
                />
              </div>
              <div className="p-field md:col-span-2">
                <FieldLabel>{t('targeting.types.keyValue.plural')}</FieldLabel>
                <div className="flex flex-col gap-2">
                  {keyValues.map((kv, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Input
                        value={kv.key}
                        onChange={(event) =>
                          updateKeyValue(index, { ...kv, key: event.target.value })
                        }
                        placeholder={t('diagnostics.key')}
                        aria-label={t('diagnostics.key')}
                        className="w-48 shrink-0"
                      />
                      <TokenInput
                        value={kv.values}
                        onChange={(next) => updateKeyValue(index, { ...kv, values: next })}
                        placeholder={t('diagnostics.values')}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('common.remove')}
                        disabled={keyValues.length === 1}
                        onClick={() => setKeyValues(keyValues.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    onClick={() => setKeyValues([...keyValues, EMPTY_KEY_VALUE])}
                  >
                    <Plus className="size-4" />
                    {t('diagnostics.addKeyValue')}
                  </Button>
                </div>
              </div>
              <div className="p-field">
                <FieldLabel>{t('diagnostics.latLong')}</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    value={latitude}
                    onChange={(event) => onChange({ latitude: event.target.value })}
                    placeholder={t('diagnostics.latitude')}
                    aria-label={t('diagnostics.latitude')}
                    aria-invalid={!!latLongError}
                    inputMode="decimal"
                  />
                  <span className="text-muted-foreground">—</span>
                  <Input
                    value={longitude}
                    onChange={(event) => onChange({ longitude: event.target.value })}
                    placeholder={t('diagnostics.longitude')}
                    aria-label={t('diagnostics.longitude')}
                    aria-invalid={!!latLongError}
                    inputMode="decimal"
                  />
                </div>
                {latLongError && <p className="text-xs text-destructive">{latLongError}</p>}
              </div>
              <div className="p-field">
                <FieldLabel tooltip={t('diagnostics.ipHelp')} htmlFor="diagnostics-ip">
                  {t('diagnostics.ip')}
                </FieldLabel>
                <Input
                  id="diagnostics-ip"
                  value={ip}
                  onChange={(event) => onChange({ ip: event.target.value })}
                  placeholder="203.0.113.7"
                  aria-invalid={!!ipError}
                />
                {ipError && <p className="text-xs text-destructive">{ipError}</p>}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={run} disabled={!canRun || running}>
          {running ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
          {t('diagnostics.run')}
        </Button>
        {adUnits.length === 0 && !adUnitsLoading && (
          <span className="text-sm text-muted-foreground">{t('diagnostics.pickAdUnits')}</span>
        )}
        {adUnits.length > 0 && !requestType && (
          <span className="text-sm text-muted-foreground">{t('diagnostics.pickRequestType')}</span>
        )}
        {!targetingOpen && (ipError || latLongError) && (
          <span className="text-sm text-destructive">{t('diagnostics.fixTargeting')}</span>
        )}
      </div>

      {submission && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('diagnostics.results')}</h2>
          {submission.adUnits.map((adUnit, index) => (
            <DiagnosticResultCard
              key={`${submission.id}-${adUnit.id}`}
              adUnit={adUnit}
              result={outcomes[index]?.result}
              queued={outcomes[index]?.status === 'queued'}
              loading={outcomes[index]?.status === 'running'}
              error={outcomes[index]?.error}
              lineItemNames={lineItemNames}
            />
          ))}
        </div>
      )}
    </div>
  )
}
